import { z } from 'zod';

/**
 * The read-only TimeLog reference data served under `TIMELOG_MODE`
 * (ADR 0035). Today: the tenant hourly-rate catalog behind GET /api/tlp/rates.
 */

// ─── Hourly-rate catalog (INTERIM role/rate source, see phoenix-review §6.8) ───

/**
 * One entry of the tenant's default hourly-rate list (`GET /v1/hourly-rate/
 * default`) — the role vocabulary (D1). `serviceId` is the role's identity and
 * what a `RoleRef.tlpId` stores; `tlpId` is the priced row (`HourlyRateID`),
 * kept for reference only — it changes when the amount does. `rate` is the
 * currently valid amount: TimeLog exposes no schedule here (verified
 * 2026-09-14), so this is also what seeds a new T&M card.
 */
export const TimeLogRateOptionSchema = z.object({
  tlpId: z.string().min(1),
  serviceId: z.string().min(1),
  name: z.string(),
  rate: z.number(),
  currency: z.string(),
  /** The legal entity the rate belongs to (TimeLog is multi-entity; D18). Null when TimeLog omitted it. */
  legalEntityId: z.string().nullable(),
  isDefault: z.boolean(),
  /**
   * The role's cost price per hour (ADR 0042), present only when a source
   * supplies one: the prototype stand-in (`COST_PRICE_SOURCE=stand-in`) hands
   * every role one of the entity's standard cost prices (`/v1/cost-price`) by a
   * stable hash. Snapshotted onto the `RoleRef` at pick time; seeds a T&M card
   * entry's `costRate`.
   */
  costRate: z.number().min(0).optional(),
});
export type TimeLogRateOption = z.infer<typeof TimeLogRateOptionSchema>;

/** The legal entity a rate list was scoped to — the caller's (D18). */
export const TimeLogLegalEntitySchema = z.object({
  id: z.string().min(1),
  name: z.string(),
  currency: z.string(),
});
export type TimeLogLegalEntity = z.infer<typeof TimeLogLegalEntitySchema>;

/**
 * The role vocabulary as one user sees it: the ACTIVE default hourly rates of
 * their legal entity, in that entity's currency, one per ServiceID (D18/D19).
 * `legalEntity` says whose list it is, so an empty picker can name it.
 */
export const TimeLogRatesResponseSchema = z.object({
  items: z.array(TimeLogRateOptionSchema),
  legalEntity: TimeLogLegalEntitySchema,
});
export type TimeLogRatesResponse = z.infer<typeof TimeLogRatesResponseSchema>;
